/**
 * PDF Export Modal - Allows users to specify filename and initiate PDF export
 */
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, FileText, Download } from 'lucide-react';
import { PDFDataService } from '../services/PDFDataService';
import { PDFGeneratorService } from '../services/PDFGeneratorService';

interface PDFExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  organizationName?: string;
  organizationId?: string;
  currentUser?: any;
}

export default function PDFExportModal({ 
  isOpen, 
  onClose, 
  organizationName = 'Organization',
  organizationId,
  currentUser
}: PDFExportModalProps) {
  const [filename, setFilename] = useState(() => {
    const date = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
    return `${organizationName} Content Export - ${date}`;
  });
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleExport = async () => {
    if (!filename.trim()) {
      setError('Please enter a filename');
      return;
    }

    if (!organizationId || !currentUser) {
      setError('Missing organization or user information');
      return;
    }

    setIsExporting(true);
    setError(null);

    try {
      // Phase 3: Collect all data needed for PDF generation
      console.log('📊 Collecting export data...');
      const exportData = await PDFDataService.collectExportData(organizationId, currentUser);
      
      console.log('✅ Data collection complete:', {
        organization: exportData.organization.companyName,
        pages: exportData.content.children.length + 1, // +1 for homepage
        tiles: countTiles(exportData.content),
        tocEntries: exportData.tableOfContents.length
      });
      
      
      // Phase 4: Generate actual PDF using jsPDF
      console.log('🎨 Generating PDF document...');
      const pdfGenerator = new PDFGeneratorService();
      await pdfGenerator.generatePDF(exportData, filename.trim());
      
      console.log('✅ PDF export complete!');
      
      onClose();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Export failed';
      setError(errorMessage);
      console.error('Export error:', err);
    } finally {
      setIsExporting(false);
    }
  };

  // Helper function to count total tiles recursively
  const countTiles = (pageData: any): number => {
    let count = pageData.tiles.length;
    pageData.children.forEach((child: any) => {
      count += countTiles(child);
    });
    return count;
  };

  // Helper function to find a tile by name recursively
  const findTileByName = (pageData: any, tileName: string): any => {
    // Check tiles in current page
    const tile = pageData.tiles.find((t: any) => t.title === tileName);
    if (tile) return tile;
    
    // Check child pages recursively
    for (const child of pageData.children) {
      const found = findTileByName(child, tileName);
      if (found) return found;
    }
    
    return null;
  };

  const handleFilenameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilename(e.target.value);
    if (error) setError(null); // Clear error when user types
  };

  // Handle keyboard events globally when modal is open
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      } else if (e.key === 'Enter' && !isExporting) {
        // Only trigger export if no input is focused
        const activeElement = document.activeElement;
        const isInputFocused = activeElement?.tagName === 'INPUT';
        
        if (!isInputFocused) {
          e.preventDefault();
          handleExport();
        }
      }
    };

    // Add event listener with capture to intercept before it reaches parent
    document.addEventListener('keydown', handleKeyDown, true);

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [isOpen, isExporting, onClose, handleExport]);

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !isExporting) {
      e.preventDefault();
      handleExport();
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <FileText className="h-6 w-6 text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900">Export to PDF</h2>
          </div>
          <button
            onClick={onClose}
            disabled={isExporting}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          <div>
            <p className="text-sm text-gray-600 mb-4">
              Export all site content to a PDF document with organization branding, 
              table of contents, and hierarchical structure.
            </p>
          </div>

          {/* Filename Input */}
          <div>
            <label htmlFor="filename" className="block text-sm font-medium text-gray-700 mb-2">
              Filename
            </label>
            <div className="relative">
                <input
                id="filename"
                type="text"
                value={filename}
                onChange={handleFilenameChange}
                onKeyDown={handleInputKeyDown}
                disabled={isExporting}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder="Enter filename (without .pdf extension)"
              />
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <span className="text-gray-500 text-sm">.pdf</span>
              </div>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-md p-3">
              {error}
            </div>
          )}

          {/* Export Progress */}
          {isExporting && (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
              <div className="flex items-center space-x-3">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                <div className="flex-1">
                  <span className="text-blue-800 text-sm font-medium">Generating PDF...</span>
                  <div className="text-blue-600 text-xs mt-1">
                    Collecting data → Creating document → Adding content
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            disabled={isExporting}
            className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleExport}
            disabled={isExporting || !filename.trim()}
            className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
          >
            <Download className="h-4 w-4" />
            <span>{isExporting ? 'Exporting...' : 'Export PDF'}</span>
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
